import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LANG, LANGUAGES, translations } from "@/i18n/translations";

const STORAGE_KEY = "atlas_lang";

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (path) => path,
  messages: translations[DEFAULT_LANG],
});

function lookup(obj, path) {
  if (!path) return "";
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return path;
    cur = cur[p];
  }
  return cur == null ? path : cur;
}

function detectInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && translations[stored]) return stored;
  } catch {
    // ignore storage errors
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    const short = navigator.language.slice(0, 2).toLowerCase();
    if (translations[short]) return short;
  }
  return DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => detectInitialLang());

  const setLang = useCallback((next) => {
    if (!translations[next]) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = translations[lang]?.lang || lang;
    }
  }, [lang]);

  const value = useMemo(() => {
    const messages = translations[lang] || translations[DEFAULT_LANG];
    return {
      lang,
      setLang,
      messages,
      languages: LANGUAGES,
      t: (path) => lookup(messages, path),
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
